import { ArrowBack } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Text } from '~/components';
import { useAppContext } from '~/contexts';
import { useTheme } from '~/themes';
import { ContentContainer } from './content.container';

interface OneColumnLayoutProps extends PropsWithChildren {
  title?: string;
  backTo?: string;
  description?: string;
}

const OneColumnContainer: FC<OneColumnLayoutProps> = ({ backTo, title, description, children }) => {
  const { COLORS } = useTheme();
  const { isMobile } = useAppContext();
  const navigate = useNavigate();

  const renderTitle = (title: ReactNode) => {
    if (title === undefined) return <></>;

    return (
      <Stack
        direction={'row'}
        alignItems={'center'}
        gap={{ md: 2, xs: 1 }}
        sx={{
          mt: { md: 0, xs: 2 },
          ml: { md: 0, xs: 0 },
        }}
      >
        {backTo && (
          <IconButton
            onClick={() => navigate(backTo, { replace: true })}
            sx={{
              width: { md: 6, xs: 4 },
              height: { md: 4, xs: 3 },
              paddingX: { md: 2, xs: 1 },
              paddingY: { md: 2, xs: 1 / 2 },
              borderRadius: '4px',
              border: '1px solid',
              borderColor: COLORS.primary.main,
              color: COLORS.primary.main,
            }}
          >
            <ArrowBack
              sx={{ width: { md: '20px', xs: '18px' }, height: { md: '20px', xs: '18px' }, color: COLORS.primary.main }}
            />
          </IconButton>
        )}
        {title}
      </Stack>
    );
  };

  if (isMobile === true) {
    return (
      <Box sx={{ marginBottom: 10 }}>
        <ContentContainer sx={{ paddingX: 2, paddingBottom: 2, paddingTop: 3 }}>
          <Stack direction={'column'} sx={{ marginBottom: 3 }}>
            {renderTitle(<Header variant={'h2'}>{title}</Header>)}
            {description && (
              <Typography
                sx={{
                  fontSize: '14px',
                }}
              >
                {description}
              </Typography>
            )}
          </Stack>
          {children}
        </ContentContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: 3, marginBottom: { md: 3, xs: 0 } }}>
      <ContentContainer sx={{ paddingX: 3, paddingBottom: 3, paddingTop: 4 }}>
        <Stack direction={'column'} sx={{ marginBottom: 3 }}>
          {renderTitle(<Header variant={'h1'}>{title}</Header>)}
          {description && (
            <Text variant='body1' fontSize={14}>
              {description}
            </Text>
          )}
        </Stack>
        {children}
      </ContentContainer>
    </Box>
  );
};

export { OneColumnContainer };
